import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import SimpleLoader from '../components/SimpleLoader';
import { getFileOpenings } from '../services/fileOpenings';
import toast from 'react-hot-toast';
import Pagination from '../components/Pagination';
import TableContainer from '../components/TableContainer';

import { deleteFileOpening } from '../services/fileOpenings';

import ConfirmationModal from '../components/ConfirmationModal';
import { useAuth } from '../contexts/AuthContext';

const FileOpeningsList = () => {
  const { isAdmin } = useAuth();
  const [fileOpenings, setFileOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState(null);

  useEffect(() => {
    const fetchFileOpenings = async () => {
      try {
        const offset = (currentPage - 1) * limit;
        const { fileOpenings, count } = await getFileOpenings({ limit, offset, sortBy, sortOrder, searchTerm });
        setFileOpenings(fileOpenings);
        setTotal(count);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchFileOpenings();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [limit, currentPage, sortBy, sortOrder, searchTerm]);

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const handleSortByChange = (e) => {
    const [newSortBy, newSortOrder] = e.target.value.split('_');
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const openModal = (id) => {
    setSelectedFileId(id);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedFileId(null);
    setIsModalOpen(false);
  };

  const handleDelete = async () => {
    if (selectedFileId) {
      try {
        await deleteFileOpening(selectedFileId);
        toast.success('File opening deleted successfully!');
        // Refetch the data to update the list
        const offset = (currentPage - 1) * limit;
        const { fileOpenings, count } = await getFileOpenings({ limit, offset, sortBy, sortOrder, searchTerm });
        setFileOpenings(fileOpenings);
        setTotal(count);
      } catch (error) {
        toast.error(error.message);
      }
      closeModal();
    }
  };

  return (
    <DashboardLayout pageTitle="File Openings">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} className="dark:text-gray-100 mr-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2" />
          <select value={limit} onChange={handleLimitChange} className="dark:text-gray-100 mr-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2">
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <select value={`${sortBy}_${sortOrder}`} onChange={handleSortByChange} className="dark:text-gray-100 mr-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2">
            <option value="id_desc">ID (Z-A)</option>
            <option value="id_asc">ID (A-Z)</option>
            <option value="date_desc">Date (Newest First)</option>
            <option value="date_asc">Date (Oldest First)</option>
            <option value="forename_asc">Client (A-Z)</option>
            <option value="forename_desc">Client (Z-A)</option>
          </select>
        </div>
        <Link to="/file-opening/create" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow">
          New File Opening
        </Link>
      </div>
      <TableContainer>
        {loading ? (
          <SimpleLoader />
        ) : (
          <>
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-6 py-3">File Ref</th>
                  <th scope="col" className="px-6 py-3">Client</th>
                  <th scope="col" className="px-6 py-3">Date</th>
                  <th scope="col" className="px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {fileOpenings.length > 0 ? (
                  fileOpenings.map((file) => (
                    <tr key={file.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4">{"ska-" + file.id}</td>
                      <td className="px-6 py-4">{`${file.forename} ${file.surname}`}</td>
                      <td className="px-6 py-4">{file.date}</td>
                      <td className="px-6 py-4">
                        <Link to={`/file-opening/${file.id}`} className="text-blue-600 hover:underline mr-4">View</Link>
                        <Link to={`/file-opening/${file.id}/edit`} className="text-blue-600 hover:underline mr-4">Edit</Link>
                        {isAdmin && <button onClick={() => openModal(file.id)} className="text-red-600 hover:underline mr-4">Delete</button>}
                        {isAdmin && <Link to={`/file-opening/${file.id}/invoices`} className="bg-blue-600 hover:bg-blue-800 rounded p-2 text-xs text-white mr-4">Invoices</Link>}
                        {isAdmin && <Link to={`/file-opening/${file.id}/receipts`} className="bg-blue-600 hover:bg-blue-800 rounded p-2 text-xs text-white mr-4">Receipts</Link>}
                        {isAdmin && <Link to={`/file-opening/${file.id}/ledger`} className="bg-blue-600 hover:bg-blue-800 rounded p-2 text-xs text-white mr-4">Client Ledger</Link>}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      No file openings found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pagination
              currentPage={currentPage}
              total={total}
              limit={limit}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </TableContainer>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this file opening?"
      />
    </DashboardLayout>
  );
};

export default FileOpeningsList;
